'use client';

import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2CheckRunResponse, PrV2ReviewResponse, PrV2SummaryResponse, PrV2ViewerMode } from '@/lib/pr-v2/types';
import { memo, useEffect, useRef, useSyncExternalStore } from 'react';
import { PrV2DescriptionWithDemo } from './demo/pr-v2-description-with-demo';
import { PrV2Header } from './header/pr-v2-header';
import { PrV2ClientStore } from './pr-v2-client-store';
import { PrV2FileFilterProvider } from './pr-v2-file-filter-context';
import { PrV2RealtimeUpdates } from './pr-v2-realtime-updates';
import { PrV2Tabs } from './pr-v2-tabs';
import { PR_V2_CONTENT_CLASS } from './shared/pr-v2-layout-classes';
import { getPrV2BaseLabel } from './shared/pr-v2-pull-request-adapter';
import { PrV2LoadingSkeleton } from './shared/skeletons/pr-v2-loading-skeleton';
import { usePrV2SkeletonLayout } from './shared/skeletons/use-pr-v2-skeleton-layout';
import { PrV2PublicSidebar } from './sidebar/pr-v2-public-sidebar';
import { PrV2Sidebar } from './sidebar/pr-v2-sidebar';
import { PrLinkHoverCards } from '@/app/(home)/code/_prototype/pr-link-hover-card';
import { PrV2OnboardingDialog } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/pr-v2-onboarding-dialog';
import { useBusyPrInstallPrompt } from '@/components/install-prompts/use-busy-pr-install-prompt';
import { MARKDOWN_RENDERER_LINK_SELECTOR } from '@/components/markdown-renderer/markdown-links';
import { useSidebar } from '@/components/ui/sidebar';
import { usePrV2Checks } from '@/data/v2/pr/use-pr-v2-checks';
import { usePrV2Reviews } from '@/data/v2/pr/use-pr-v2-reviews';
import { usePrV2Summary } from '@/data/v2/pr/use-pr-v2-summary';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { PR_V2_VIEWER_MODE } from '@/lib/pr-v2/types';
import { usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';
import { useZenModeStore } from '@/lib/stores/zen-mode-store';
import { cn } from '@/lib/utils';
type PrV2PageClientProps = {
  owner: string;
  repo: string;
  number: string;
  viewerMode: PrV2ViewerMode;
  initialSummary?: PrV2SummaryResponse | null;
  initialSummaryUpdatedAt?: number;
  initialReviews?: PrV2ReviewResponse[];
  initialChecks?: PrV2CheckRunResponse[];
  initialMergeabilityStatus?: MergeabilityStatus | null;
  utmSource?: string;
};
const MemoPrV2Header = memo(PrV2Header);
const MemoPrV2Sidebar = memo(PrV2Sidebar);
const MemoPrV2PublicSidebar = memo(PrV2PublicSidebar);

// Module-scope so useSyncExternalStore never re-subscribes between renders.
const subscribeToViewPreferencesHydration = (onHydrated: () => void) => usePrV2ViewPreferencesStore.persist.onFinishHydration(onHydrated);
const getHasHydratedViewPreferences = () => usePrV2ViewPreferencesStore.persist.hasHydrated();
const getServerHasHydratedViewPreferences = () => false;
export function PrV2PageClient(props) {
  const $ = _c(5);
  const t0 = `${props.owner}/${props.repo}/${props.number}`;
  let t1;
  if ($[0] !== props) {
    t1 = <PrV2PageClientInner {...props} />;
    $[0] = props;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = <PrV2FileFilterProvider key={t0}>{t1}</PrV2FileFilterProvider>;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function PrV2PageClientInner(t0) {
  const $ = _c(117);
  const {
    owner,
    repo,
    number,
    viewerMode,
    initialSummary,
    initialSummaryUpdatedAt,
    initialReviews,
    initialChecks,
    initialMergeabilityStatus,
    utmSource
  } = t0;
  const {
    open: isChatSidebarOpen
  } = useSidebar();
  const isZenMode = useZenModeStore(_temp);
  const hasHydratedViewPreferences = useSyncExternalStore(subscribeToViewPreferencesHydration, getHasHydratedViewPreferences, getServerHasHydratedViewPreferences);
  const t1 = initialSummary ?? undefined;
  let t2;
  if ($[0] !== initialSummaryUpdatedAt || $[1] !== t1) {
    t2 = {
      initialData: t1,
      initialDataUpdatedAt: initialSummaryUpdatedAt
    };
    $[0] = initialSummaryUpdatedAt;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const summaryQuery = usePrV2Summary(owner, repo, number, t2);
  const summaryResponse = summaryQuery.data;
  const summary = summaryResponse?.pullRequest;
  const hasSummary = Boolean(summary);
  const seededMergeabilityStatus = initialMergeabilityStatus ?? null;
  const skeletonLayout = usePrV2SkeletonLayout();
  useBusyPrInstallPrompt(summary, viewerMode === PR_V2_VIEWER_MODE.MEMBER);
  const trackedPrRef = useRef(null);
  let t3;
  if ($[3] !== hasHydratedViewPreferences || $[4] !== hasSummary || $[5] !== number || $[6] !== owner || $[7] !== repo || $[8] !== summary?.installationId || $[9] !== utmSource || $[10] !== viewerMode) {
    t3 = () => {
      if (!hasHydratedViewPreferences || !hasSummary || viewerMode !== PR_V2_VIEWER_MODE.MEMBER) {
        return;
      }
      const installationId = summary?.installationId;
      const prKey = `${owner}/${repo}/${number}`;
      if (trackedPrRef.current === prKey) {
        return;
      }
      trackedPrRef.current = prKey;
      trackClientEvent("v2_pr_viewed", installationId?.toString() ?? null, {
        owner,
        repo,
        pr_number: number,
        is_story_mode: true,
        ...(utmSource !== undefined && {
          utm_source: utmSource
        })
      });
      fetch(`/api/v2/pull-requests/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/${encodeURIComponent(number)}/view`, {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          utmSource
        }),
        credentials: "same-origin",
        keepalive: true
      }).catch(_temp2);
    };
    $[3] = hasHydratedViewPreferences;
    $[4] = hasSummary;
    $[5] = number;
    $[6] = owner;
    $[7] = repo;
    $[8] = summary?.installationId;
    $[9] = utmSource;
    $[10] = viewerMode;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = summary?.installationId;
  let t5;
  if ($[12] !== hasHydratedViewPreferences || $[13] !== hasSummary || $[14] !== number || $[15] !== owner || $[16] !== repo || $[17] !== t4 || $[18] !== utmSource || $[19] !== viewerMode) {
    t5 = [hasHydratedViewPreferences, hasSummary, owner, repo, number, t4, utmSource, viewerMode];
    $[12] = hasHydratedViewPreferences;
    $[13] = hasSummary;
    $[14] = number;
    $[15] = owner;
    $[16] = repo;
    $[17] = t4;
    $[18] = utmSource;
    $[19] = viewerMode;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  useEffect(t3, t5);
  let t6;
  if ($[21] !== hasSummary || $[22] !== initialReviews) {
    t6 = {
      enabled: hasSummary,
      initialData: initialReviews
    };
    $[21] = hasSummary;
    $[22] = initialReviews;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const reviewsQuery = usePrV2Reviews(owner, repo, number, t6);
  const t7 = summary?.headSha ?? null;
  let t8;
  if ($[24] !== hasSummary || $[25] !== initialChecks || $[26] !== t7) {
    t8 = {
      enabled: hasSummary,
      initialData: initialChecks,
      currentHeadSha: t7
    };
    $[24] = hasSummary;
    $[25] = initialChecks;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const checksQuery = usePrV2Checks(owner, repo, number, t8);
  if (summaryQuery.isLoading) {
    let t9;
    if ($[28] !== skeletonLayout) {
      t9 = <PrV2LoadingSkeleton layout={skeletonLayout} />;
      $[28] = skeletonLayout;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    return t9;
  }
  if (summaryQuery.error) {
    const t9 = summaryQuery.error instanceof Error ? summaryQuery.error.message : "Failed to load PR v2 data";
    let t10;
    if ($[30] !== t9) {
      t10 = <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{t9}</div>;
      $[30] = t9;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    return t10;
  }
  if (!summary) {
    let t9;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="rounded-lg border border-zinc-200 bg-white p-4 text-sm text-zinc-600">Pull request not found.</div>;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    return t9;
  }
  let t9;
  if ($[33] !== reviewsQuery.data) {
    t9 = reviewsQuery.data ?? [];
    $[33] = reviewsQuery.data;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const reviews = t9;
  let t10;
  if ($[35] !== checksQuery.data) {
    t10 = checksQuery.data ?? [];
    $[35] = checksQuery.data;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const checks = t10;
  const pullRequest = summary;
  const metadataSummary = pullRequest.metadataSummary;
  let t11;
  if ($[37] !== number || $[38] !== owner || $[39] !== pullRequest.id || $[40] !== pullRequest.installationId || $[41] !== pullRequest.repositoryId || $[42] !== repo || $[43] !== viewerMode) {
    t11 = viewerMode === PR_V2_VIEWER_MODE.MEMBER ? <><PrV2ClientStore owner={owner} repo={repo} number={number} installationId={pullRequest.installationId} repositoryId={pullRequest.repositoryId ?? null} pullRequestId={pullRequest.id} /><PrV2RealtimeUpdates owner={owner} repo={repo} number={number} installationId={pullRequest.installationId} /></> : null;
    $[37] = number;
    $[38] = owner;
    $[39] = pullRequest.id;
    $[40] = pullRequest.installationId;
    $[41] = pullRequest.repositoryId;
    $[42] = repo;
    $[43] = viewerMode;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== number || $[46] !== owner || $[47] !== repo || $[48] !== viewerMode) {
    t12 = viewerMode === PR_V2_VIEWER_MODE.MEMBER ? <PrV2OnboardingDialog key={`${owner}/${repo}/${number}`} /> : null;
    $[45] = number;
    $[46] = owner;
    $[47] = repo;
    $[48] = viewerMode;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  const t13 = viewerMode === PR_V2_VIEWER_MODE.PUBLIC;
  let t14;
  if ($[50] !== metadataSummary || $[51] !== number || $[52] !== owner || $[53] !== pullRequest || $[54] !== repo || $[55] !== seededMergeabilityStatus || $[56] !== t13) {
    t14 = <div className={PR_V2_CONTENT_CLASS}><MemoPrV2Header owner={owner} repo={repo} number={number} summary={pullRequest} metadata={metadataSummary} readOnly={t13} initialMergeabilityStatus={seededMergeabilityStatus} /></div>;
    $[50] = metadataSummary;
    $[51] = number;
    $[52] = owner;
    $[53] = pullRequest;
    $[54] = repo;
    $[55] = seededMergeabilityStatus;
    $[56] = t13;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  let t15;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn(PR_V2_CONTENT_CLASS, "mt-2 order-2 md:order-0");
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  const t16 = viewerMode === PR_V2_VIEWER_MODE.PUBLIC;
  const t17 = summaryQuery.isFetching && summaryQuery.dataUpdatedAt === 0;
  const t18 = viewerMode === PR_V2_VIEWER_MODE.MEMBER;
  let t19;
  if ($[59] !== metadataSummary.body || $[60] !== number || $[61] !== owner || $[62] !== pullRequest.headSha || $[63] !== pullRequest.installationId || $[64] !== repo || $[65] !== t16 || $[66] !== t17 || $[67] !== t18) {
    t19 = <div className={t15}><PrV2DescriptionWithDemo owner={owner} repo={repo} number={number} body={metadataSummary.body} readOnly={t16} isAwaitingFullData={t17} installationId={pullRequest.installationId} headSha={pullRequest.headSha} showsDemo={t18} /></div>;
    $[59] = metadataSummary.body;
    $[60] = number;
    $[61] = owner;
    $[62] = pullRequest.headSha;
    $[63] = pullRequest.installationId;
    $[64] = repo;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  const t20 = isChatSidebarOpen ? "3xl:-ml-40" : "2xl:-ml-40";
  const t21 = isZenMode ? "max-w-0 overflow-hidden opacity-0" : "";
  let t22;
  if ($[69] !== t20 || $[70] !== t21) {
    t22 = cn("col-start-1 col-end-13 order-3 max-md:px-3 md:col-start-10 md:col-end-12 md:order-0 md:row-start-2", "md:group-has-[#pr-v2-demo]/pr-top:pt-[17px]", "transition-opacity duration-200 ease-linear", t20, t21);
    $[69] = t20;
    $[70] = t21;
    $[71] = t22;
  } else {
    t22 = $[71];
  }
  let t23;
  if ($[72] !== checks || $[73] !== checksQuery.error || $[74] !== checksQuery.isLoading || $[75] !== metadataSummary || $[76] !== number || $[77] !== owner || $[78] !== pullRequest.author || $[79] !== pullRequest.headSha || $[80] !== pullRequest.installationId || $[81] !== repo || $[82] !== reviews || $[83] !== seededMergeabilityStatus || $[84] !== viewerMode) {
    t23 = viewerMode === PR_V2_VIEWER_MODE.PUBLIC ? <MemoPrV2PublicSidebar headSha={pullRequest.headSha} metadata={metadataSummary} reviews={reviews} checks={checks} initialMergeabilityStatus={seededMergeabilityStatus} checksLoading={checksQuery.isLoading} checksError={checksQuery.error instanceof Error ? checksQuery.error.message : checksQuery.error ? "Failed to load check runs" : null} /> : <MemoPrV2Sidebar owner={owner} repo={repo} number={number} headSha={pullRequest.headSha} authorLogin={pullRequest.author.login} installationId={pullRequest.installationId} metadata={metadataSummary} reviews={reviews} checks={checks} initialMergeabilityStatus={seededMergeabilityStatus} checksLoading={checksQuery.isLoading} checksError={checksQuery.error instanceof Error ? checksQuery.error.message : checksQuery.error ? "Failed to load check runs" : null} />;
    $[72] = checks;
    $[73] = checksQuery.error;
    $[74] = checksQuery.isLoading;
    $[75] = metadataSummary;
    $[76] = number;
    $[77] = owner;
    $[78] = pullRequest.author;
    $[79] = pullRequest.headSha;
    $[80] = pullRequest.installationId;
    $[81] = repo;
    $[82] = reviews;
    $[83] = seededMergeabilityStatus;
    $[84] = viewerMode;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] !== t22 || $[87] !== t23) {
    t24 = <div className={t22}>{t23}</div>;
    $[86] = t22;
    $[87] = t23;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== t14 || $[90] !== t19 || $[91] !== t24) {
    t25 = <div className="bg-background pb-5 pt-6"><div className="group/pr-top grid w-full grid-cols-12 gap-4">{t14}{t19}{t24}</div></div>;
    $[89] = t14;
    $[90] = t19;
    $[91] = t24;
    $[92] = t25;
  } else {
    t25 = $[92];
  }
  const t26 = pullRequest.headSha;
  const t27 = pullRequest.baseSha;
  const t28 = metadataSummary.headRef ?? null;
  const t29 = pullRequest.merged;
  const t30 = pullRequest.installationId;
  let t31;
  if ($[93] !== pullRequest) {
    t31 = getPrV2BaseLabel(pullRequest);
    $[93] = pullRequest;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  let t32;
  if ($[95] !== number || $[96] !== owner || $[97] !== pullRequest.baseSha || $[98] !== pullRequest.headSha || $[99] !== pullRequest.installationId || $[100] !== pullRequest.merged || $[101] !== repo || $[102] !== t28 || $[103] !== t31 || $[104] !== viewerMode) {
    t32 = <PrV2Tabs owner={owner} repo={repo} number={number} viewerMode={viewerMode} headSha={t26} baseSha={t27} headRef={t28} isMerged={t29} installationId={t30} baseLabel={t31} />;
    $[95] = number;
    $[96] = owner;
    $[97] = pullRequest.baseSha;
    $[98] = pullRequest.headSha;
    $[99] = pullRequest.installationId;
    $[100] = pullRequest.merged;
    $[101] = repo;
    $[102] = t28;
    $[103] = t31;
    $[104] = viewerMode;
    $[105] = t32;
  } else {
    t32 = $[105];
  }
  let t33;
  if ($[106] !== t11 || $[107] !== t12 || $[108] !== t25 || $[109] !== t32) {
    t33 = <div className="space-y-0">{t11}{t12}{t25}{t32}</div>;
    $[106] = t11;
    $[107] = t12;
    $[108] = t25;
    $[109] = t32;
    $[110] = t33;
  } else {
    t33 = $[110];
  }
  const page = t33;
  let t34;
  if ($[111] !== number || $[112] !== owner || $[113] !== page || $[114] !== repo || $[115] !== viewerMode) {
    t34 = viewerMode === PR_V2_VIEWER_MODE.MEMBER ? <PrLinkHoverCards linkSelector={MARKDOWN_RENDERER_LINK_SELECTOR} exclude={{
      owner,
      repo,
      number: Number(number)
    }}>{page}</PrLinkHoverCards> : page;
    $[111] = number;
    $[112] = owner;
    $[113] = page;
    $[114] = repo;
    $[115] = viewerMode;
    $[116] = t34;
  } else {
    t34 = $[116];
  }
  return t34;
}
function _temp2() {}
function _temp(state) {
  return state.isZenMode;
}